/**
 * MagneticButton - Premium micro-interaction button with cursor attraction
 *
 * Features:
 * - Cursor-attracted hover effect (button follows cursor within bounds)
 * - Smooth spring physics animation
 * - Subtle scale and glow on hover
 * - 200ms transitions for premium feel
 *
 * Based on Codrops magnetic buttons technique:
 * https://tympanus.net/codrops/2020/08/05/magnetic-buttons/
 */

import { type ReactNode, useRef, useState, forwardRef } from "react";
import { motion, useSpring, useMotionValue } from "framer-motion";
import { cn } from "../utils";

// Domain accent colors for the LUKHAS ecosystem
const DOMAIN_COLORS = {
  ai: { accent: "#8b7cf6", glow: "rgba(139, 124, 246, 0.4)" },
  cloud: { accent: "#38bdf8", glow: "rgba(56, 189, 248, 0.4)" },
  dev: { accent: "#06b6d4", glow: "rgba(6, 182, 212, 0.4)" },
  id: { accent: "#9333ea", glow: "rgba(147, 51, 234, 0.4)" },
  com: { accent: "#3b82f6", glow: "rgba(59, 130, 246, 0.4)" },
  io: { accent: "#22d3ee", glow: "rgba(34, 211, 238, 0.4)" },
  us: { accent: "#6366f1", glow: "rgba(99, 102, 241, 0.4)" },
  eu: { accent: "#8b5cf6", glow: "rgba(139, 92, 246, 0.4)" },
  store: { accent: "#f59e0b", glow: "rgba(245, 158, 11, 0.4)" },
  xyz: { accent: "#ec4899", glow: "rgba(236, 72, 153, 0.4)" },
  // Constellation star colors
  dreams: { accent: "#22D3EE", glow: "rgba(34, 211, 238, 0.4)" },
  memory: { accent: "#A78BFA", glow: "rgba(167, 139, 250, 0.4)" },
  vision: { accent: "#60A5FA", glow: "rgba(96, 165, 250, 0.4)" },
  identity: { accent: "#F472B6", glow: "rgba(244, 114, 182, 0.4)" },
  bio: { accent: "#34D399", glow: "rgba(52, 211, 153, 0.4)" },
  ethics: { accent: "#2DD4BF", glow: "rgba(45, 212, 191, 0.4)" },
  guardian: { accent: "#FBBF24", glow: "rgba(251, 191, 36, 0.4)" },
  quantum: { accent: "#818CF8", glow: "rgba(129, 140, 248, 0.4)" },
} as const;

export type DomainColorKey = keyof typeof DOMAIN_COLORS;

// Base props shared between button and link
interface MagneticButtonBaseProps {
  children: ReactNode;
  /** Domain or star name for accent color theming */
  domain?: DomainColorKey;
  /** Custom accent color (overrides domain) */
  accentColor?: string;
  /** Button variant */
  variant?: "primary" | "secondary" | "ghost";
  /** Size of the button */
  size?: "sm" | "md" | "lg";
  /** Magnetic strength (0-1). Default: 0.3 */
  magneticStrength?: number;
  /** Click handler */
  onClick?: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  /** Additional class names */
  className?: string;
  /** External link */
  external?: boolean;
  /** Disabled state */
  disabled?: boolean;
}

// Props when rendered as a button
export interface MagneticButtonAsButtonProps extends MagneticButtonBaseProps, Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof MagneticButtonBaseProps> {
  href?: never;
  /** Type for button element */
  type?: "button" | "submit" | "reset";
}

// Props when rendered as a link
export interface MagneticButtonAsLinkProps extends MagneticButtonBaseProps, Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof MagneticButtonBaseProps> {
  href: string;
  /** Type is not applicable to links but kept for compatibility if needed, though usually omitted */
  type?: never;
}

export type MagneticButtonProps = MagneticButtonAsButtonProps | MagneticButtonAsLinkProps;

export const MagneticButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  MagneticButtonProps
>(function MagneticButton(
  {
    children,
    domain = "ai",
    accentColor,
    variant = "primary",
    size = "md",
    magneticStrength = 0.3,
    onClick,
    className = "",
    href,
    external = false,
    disabled = false,
    type = "button",
    ...props
  },
  ref
) {
  const buttonRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  // Get colors from domain or custom accent
  const colors = accentColor
    ? { accent: accentColor, glow: `${accentColor}66` }
    : DOMAIN_COLORS[domain];

  // Detect dark mode via CSS
  const isDark =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;

  // Motion values for magnetic effect
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  // Spring physics for smooth movement
  const springConfig = { stiffness: 400, damping: 30 };
  const springX = useSpring(x, springConfig);
  const springY = useSpring(y, springConfig);

  // Handle mouse move for magnetic effect
  const handleMouseMove = (e: React.MouseEvent) => {
    if (disabled) return;
    const el = buttonRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    // Calculate distance from center
    const distanceX = e.clientX - centerX;
    const distanceY = e.clientY - centerY;

    // Apply magnetic pull (stronger when closer to center)
    x.set(distanceX * magneticStrength);
    y.set(distanceY * magneticStrength);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    x.set(0);
    y.set(0);
  };

  const handleMouseEnter = () => {
    if (!disabled) {
      setIsHovered(true);
    }
  };

  // Size styles
  const sizeStyles = {
    sm: "px-4 py-2 text-xs",
    md: "px-6 py-3 text-sm",
    lg: "px-8 py-4 text-base",
  };

  // Variant styles
  const getVariantStyles = () => {
    switch (variant) {
      case "primary":
        return {
          background: `linear-gradient(135deg, ${colors.accent}, ${colors.accent}dd)`,
          color: isDark ? "#000000" : "#ffffff",
          border: "none",
          boxShadow: isHovered
            ? `0 10px 40px ${colors.glow}, 0 0 0 1px ${colors.accent}30`
            : `0 4px 20px ${colors.accent}20`,
        };
      case "secondary":
        return {
          background: isDark
            ? "rgba(255, 255, 255, 0.05)"
            : "rgba(0, 0, 0, 0.03)",
          color: colors.accent,
          border: `1px solid ${colors.accent}40`,
          boxShadow: isHovered
            ? `0 10px 40px ${colors.accent}15, inset 0 0 20px ${colors.accent}10`
            : "none",
        };
      case "ghost":
        return {
          background: "transparent",
          color: isDark ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
          border: "none",
          boxShadow: "none",
        };
    }
  };

  const variantStyles = getVariantStyles();

  const buttonContent = (
    <motion.span
      className={cn(
        "relative inline-flex items-center justify-center gap-2",
        "rounded-full font-light tracking-wider uppercase",
        "transition-all duration-200",
        sizeStyles[size],
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
      style={{
        x: springX,
        y: springY,
        ...variantStyles,
      }}
      whileHover={disabled ? {} : { scale: 1.02 }}
      whileTap={disabled ? {} : { scale: 0.98 }}
    >
      {/* Glow effect on hover */}
      {isHovered && variant === "primary" && !disabled && (
        <motion.span
          className="absolute inset-0 rounded-full pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            background: `radial-gradient(ellipse at center, ${colors.glow} 0%, transparent 70%)`,
            filter: "blur(15px)",
          }}
        />
      )}

      <span className="relative z-10">{children}</span>
    </motion.span>
  );

  // Accessibility check for icon buttons or missing labels
  if (
    !props['aria-label'] &&
    !props['aria-labelledby'] &&
    process.env.NODE_ENV === 'development'
  ) {
    // If the only child is an object (like an SVG element) and it lacks an aria-label
    // Or if children is completely undefined
    const isIconOnly = children && typeof children === 'object' && !Array.isArray(children);
    if (!children || isIconOnly) {
      console.warn(
        'Palette UX Warning: MagneticButton that appears to be icon-only must have an aria-label or aria-labelledby for accessibility.'
      );
    }
  }

  // Common props for both button and anchor
  const commonProps = {
    onMouseMove: handleMouseMove,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    className: cn(
      "inline-block cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:rounded-full",
      disabled && "pointer-events-none"
    ),
    style: {
      "--focus-ring-color": colors.accent,
    } as React.CSSProperties,
    'aria-disabled': disabled,
  };

  // Render as link or button
  if (href) {
    const linkProps = external
      ? { target: "_blank", rel: "noopener noreferrer" }
      : {};

    return (
      <a
        {...commonProps}
        {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        ref={(element) => {
          if (typeof ref === 'function') ref(element);
          else if (ref) (ref as React.MutableRefObject<HTMLAnchorElement | null>).current = element;
          (buttonRef as React.MutableRefObject<HTMLAnchorElement | null>).current = element;
        }}
        href={disabled ? undefined : href}
        {...linkProps}
      >
        {buttonContent}
        {external && <span className="sr-only">(opens in a new tab)</span>}
      </a>
    );
  }

  return (
    <button
      {...commonProps}
      {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      ref={(element) => {
          if (typeof ref === 'function') ref(element);
          else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = element;
          (buttonRef as React.MutableRefObject<HTMLButtonElement | null>).current = element;
      }}
      onClick={disabled ? undefined : (onClick as React.MouseEventHandler<HTMLButtonElement>)}
      type={type}
      disabled={disabled}
    >
      {buttonContent}
    </button>
  );
});

MagneticButton.displayName = "MagneticButton";

/**
 * MagneticLink - Magnetic effect for text links
 */
export interface MagneticLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  children: ReactNode;
  href: string;
  domain?: DomainColorKey;
  accentColor?: string;
  external?: boolean;
  className?: string;
}

export function MagneticLink({
  children,
  href,
  domain = "ai",
  accentColor,
  external = false,
  className = "",
  onAnimationStart: _onAnimationStart, // Need to omit this to avoid Framer Motion type conflict
  onDragStart: _onDragStart,
  onDragEnd: _onDragEnd,
  onDrag: _onDrag,
  ...props
}: MagneticLinkProps) {
  const colors = accentColor
    ? { accent: accentColor, glow: `${accentColor}66` }
    : DOMAIN_COLORS[domain];

  const linkRef = useRef<HTMLAnchorElement>(null);

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 400, damping: 30 });
  const springY = useSpring(y, { stiffness: 400, damping: 30 });

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!linkRef.current) return;
    const rect = linkRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    x.set((e.clientX - centerX) * 0.2);
    y.set((e.clientY - centerY) * 0.2);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  const linkProps = external
    ? { target: "_blank", rel: "noopener noreferrer" }
    : {};

  return (
    <motion.a
      ref={linkRef}
      href={href}
      {...linkProps}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={cn(
        "inline-flex items-center gap-2 text-sm font-light tracking-wide transition-colors duration-200",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:rounded-sm",
        className
      )}
      style={{
        x: springX,
        y: springY,
        color: colors.accent,
      }}
      {...props}
    >
      {children}
      <svg
        aria-hidden="true"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path d="M5 12h14M12 5l7 7-7 7" />
      </svg>
      {external && <span className="sr-only">(opens in a new tab)</span>}
    </motion.a>
  );
}

MagneticLink.displayName = "MagneticLink";
